<template>
  <card-date-filter
    v-if="isCardType"
    :internal-interval="internalInterval"
    :filtered-items="filteredItems"
    :color="color"
    :current-selected-text="currentSelectedText"
    :localized-date-text="localizedDateText"
    :should-disabled-calendar="shouldDisabledCalendar"
    :picker-type="pickerType"
    :should-picker-accept-range="shouldPickerAcceptRange"
    :should-picker-be-readonly="shouldPickerBeReadonly"
    :min-date="minDate"
    :max-date="computedMaxDate"
    :computed-internal-value="computedInternalValue"
    :picker-date.sync="pickerDate"
    :selected-quarter.sync="selectedQuarter"
    :active-year-style="activeYearStyle"
    :year-internal-value="yearInternalValue"
    :rounded="rounded"
    :small="small"
    :should-disabled-confirm="shouldDisabledConfirm"
    :confirm-button-text="confirmButtonText"
    :cancel-button-text="cancelButtonText"
    :is-card-type="isCardType"
    @cancel="onCancel"
    @confirm="onConfirm"
    @inputDate="onInputDate"
    @changeDate="onChangeDate"
    @internalIntervalChange="onInternalIntervalChange"
  ></card-date-filter>

  <v-menu
    v-else
    v-model="isMenuOpened"
    offset-y
    content-class="mt-2"
    v-bind="menuProps"
    :close-on-content-click="false"
  >
    <template #activator="{on, attrs, value}">
      <slot name="activator" :on="on" :attrs="attrs" :value="value">
        <div style="position: relative">
          <slot name="activator" :on="on" :attrs="attrs">
            <div class="default-activator" v-bind="attrs" v-on="on">
              <div class="d-flex align-center" style="gap: 5px">
                <z-chip
                  class="flex-shrink-0"
                  x-small
                  :text="$t(`${interval}`)"
                ></z-chip>
              </div>

              <template v-if="shouldShowArrows">
                <div
                  class="prev-icon-container"
                  :class="{disabled: shouldDisablePrevArrow}"
                  style="padding-right: 1px"
                >
                  <z-icon
                    color="black"
                    size="15"
                    :disabled="shouldDisablePrevArrow"
                    :tooltip="prevArrowTooltipText"
                    @click.stop="onClickPrevArrow"
                  >
                    $chevron_left_rounded</z-icon
                  >
                </div>

                <div
                  class="next-icon-container"
                  :class="{disabled: shouldDisabledNextArrow}"
                  style="padding-left: 1px"
                >
                  <z-icon
                    color="black"
                    size="15"
                    :disabled="shouldDisabledNextArrow"
                    :tooltip="nextArrowTooltipText"
                    @click.stop="onClickNextArrow"
                  >
                    $chevron_right_rounded
                  </z-icon>
                </div>
              </template>

              <h4 class="truncate">
                {{ defaultActivatorText }}
              </h4>
              <z-icon> {{ value ? '$expand_less' : '$expand_more' }} </z-icon>
            </div>
          </slot>

          <v-fade-transition>
            <div v-if="value" class="arrow-menu"></div>
          </v-fade-transition>
        </div>
      </slot>
    </template>

    <card-date-filter
      :internal-interval="internalInterval"
      :filtered-items="filteredItems"
      :color="color"
      :current-selected-text="currentSelectedText"
      :localized-date-text="localizedDateText"
      :should-disabled-calendar="shouldDisabledCalendar"
      :picker-type="pickerType"
      :should-picker-accept-range="shouldPickerAcceptRange"
      :should-picker-be-readonly="shouldPickerBeReadonly"
      :min-date="minDate"
      :max-date="computedMaxDate"
      :computed-internal-value="computedInternalValue"
      :picker-date.sync="pickerDate"
      :selected-quarter.sync="selectedQuarter"
      :active-year-style="activeYearStyle"
      :year-internal-value="yearInternalValue"
      :rounded="rounded"
      :small="small"
      :should-disabled-confirm="shouldDisabledConfirm"
      :confirm-button-text="confirmButtonText"
      :cancel-button-text="cancelButtonText"
      :is-card-type="isCardType"
      @cancel="onCancel"
      @confirm="onConfirm"
      @inputDate="onInputDate"
      @changeDate="onChangeDate"
      @internalIntervalChange="onInternalIntervalChange"
    />
  </v-menu>
</template>

<script>
import {
  add,
  addDays,
  addMonths,
  addQuarters,
  addWeeks,
  addYears,
  differenceInDays,
  endOfMonth,
  endOfQuarter,
  endOfToday,
  endOfWeek,
  endOfYear,
  endOfYesterday,
  formatISO,
  getDaysInMonth,
  getQuarter,
  isAfter,
  isBefore,
  isSameDay,
  isSameMonth,
  isSameQuarter,
  isSameWeek,
  isSameYear,
  parseISO,
  startOfMonth,
  startOfQuarter,
  startOfToday,
  startOfWeek,
  startOfYear,
  startOfYesterday,
  subDays,
  subHours,
  subMonths,
  subQuarters,
  subWeeks,
  subYears,
} from 'date-fns'

import CardDateFilter from './private/CardDateFilter.vue'

export default {
  name: 'ZDateRangeFilter',
  components: {
    CardDateFilter,
  },
  props: {
    /**
     * Array com os valos de inicio e fim do intervalo selecionado pelo usuário
     */
    value: {
      type: Array,
      default: () => [],
      required: true,
    },

    /**
     * Intervalo selecionado pelo usuário
     */
    interval: {
      required: true,
      type: [String],
      default: 'today',
    },
    /**
     * Array que define quais intervalos estão disponiveis para seleção
     */
    intervalItems: {
      required: false,
      type: Array,
      default: () => [
        'now',
        'today',
        'yesterday',
        'day',
        'week',
        'month',
        'quarter',
        'year',
        'custom',
      ],
    },

    /**
     * Data minimia que o componente irá aceitar
     */
    minDate: {
      type: String,
      default: '2015-01-01',
    },
    /**
     * Data máxima que o componente irá aceitar
     */
    maxDate: {
      type: String,
      default: formatISO(startOfToday(), {representation: 'date'}),
    },

    /**
     * Texto do botão de cancelar
     */
    cancelButtonText: {
      type: String,
      default: '',
    },

    /**
     * Texto do botão de confirmar
     */
    confirmButtonText: {
      type: String,
      default: '',
    },

    /**
     * Customização dos intervalos disponíveis para seleção
     * @type {Array<{text: string, value: [ISODate, ISODate]}>}
     */
    customIntervals: {
      type: Array,
      default: () => [],
      required: false,
    },

    menuProps: {
      type: Object,
      default: () => ({}),
    },

    color: {
      type: String,
      default: 'var(--primary)',
    },
    /**
     * flag para diminuir o botão de ação
     */
    small: {
      type: Boolean,
      default: false,
    },
    /**
     * flag para deixar o botão com border-radius maior
     */
    rounded: {
      type: Boolean,
      default: false,
    },
    /**
     * flag para ativar o template de card
     */
    isCardType: {
      type: Boolean,
      default: false,
    },
    description: {
      type: String,
      default: 'Selecione um intervalo',
    },
  },

  data() {
    return {
      internalValue: '',
      dayInternalValue: '',
      weekInternalValue: '',
      monthInternalValue: '',
      yearInternalValue: '',
      selectedQuarter: '',
      pickerDate: '',
      isMenuOpened: false,
      internalInterval: '',
    }
  },

  computed: {
    computedMaxDate() {
      if (
        this.internalInterval === 'now' ||
        this.internalInterval === 'today' ||
        this.internalInterval === 'yesterday' ||
        this.internalInterval === 'day' ||
        this.internalInterval === 'custom'
      ) {
        return this.maxDate
      }

      if (this.internalInterval === 'week') {
        const endOfPrevWeek = endOfWeek(subWeeks(new Date(), 1), {
          weekStartsOn: 1,
        })

        return formatISO(endOfPrevWeek, {representation: 'date'})
      }

      if (this.internalInterval === 'month') {
        const endOfPrevMonth = endOfMonth(subMonths(new Date(), 1))

        return formatISO(endOfPrevMonth, {representation: 'date'})
      }

      if (this.internalInterval === 'quarter') {
        const endOfPrevQuarter = endOfQuarter(subQuarters(new Date(), 1))

        return formatISO(endOfPrevQuarter, {representation: 'date'})
      }

      if (this.internalInterval === 'year') {
        const endOfPrevYear = endOfYear(subYears(new Date(), 1))

        return formatISO(endOfPrevYear, {representation: 'date'})
      }

      return this.maxDate
    },

    computedInternalValue() {
      switch (this.internalInterval) {
        case 'day':
          return this.dayInternalValue

        case 'week':
          return this.weekInternalValue

        case 'month':
          return this.monthInternalValue

        default:
          return this.internalValue
      }
    },

    filteredItems() {
      if (this.customIntervals.length > 0) {
        return [
          {
            text: this.$t('custom'),
            value: 'custom',
          },

          {
            divider: true,
          },

          ...this.customIntervals,
        ]
      }

      return [
        {
          text: this.$t('now'),
          value: 'now',
        },
        {
          text: this.$t('today'),
          value: 'today',
        },
        {
          text: this.$t('yesterday'),
          value: 'yesterday',
        },

        {
          divider: true,
        },

        {
          text: this.$t('day'),
          value: 'day',
        },

        {
          text: this.$t('week'),
          value: 'week',
        },

        {
          text: this.$t('month'),
          value: 'month',
        },

        {
          text: this.$t('quarter'),
          value: 'quarter',
        },

        {
          text: this.$t('year'),
          value: 'year',
        },

        {
          divider: true,
        },

        {
          text: this.$t('custom'),
          value: 'custom',
        },
      ].filter(
        (item) => this.intervalItems.includes(item.value) || item.divider
      )
    },

    today() {
      return formatISO(startOfToday(), {representation: 'date'})
    },

    shouldDisabledConfirm() {
      return (
        this.internalInterval === 'custom' && this.internalValue.length !== 2
      )
    },

    shouldPickerBeReadonly() {
      return (
        this.internalInterval === 'today' ||
        this.internalInterval === 'yesterday'
      )
    },

    shouldPickerAcceptRange() {
      return (
        this.internalInterval === 'week' ||
        this.internalInterval === 'custom' ||
        Boolean(this.customIntervals.length)
      )
    },

    shouldDisabledCalendar() {
      return (
        this.internalInterval === 'now' ||
        this.internalInterval === 'today' ||
        this.internalInterval === 'yesterday'
      )
    },

    shouldShowArrows() {
      return (
        this.interval !== 'now' &&
        this.interval !== 'today' &&
        this.interval !== 'yesterday' &&
        this.interval !== 'custom' &&
        !this.customIntervals.length
      )
    },

    shouldDisabledNextArrow() {
      if (this.internalInterval === 'day') {
        return isSameDay(parseISO(this.dayInternalValue), endOfToday())
      }

      if (this.internalInterval === 'week') {
        return isSameDay(
          endOfWeek(parseISO(this.weekInternalValue[1]), {weekStartsOn: 1}),
          endOfWeek(parseISO(this.computedMaxDate), {weekStartsOn: 1})
        )
      }

      if (this.internalInterval === 'month') {
        return isSameDay(
          endOfMonth(parseISO(this.monthInternalValue)),
          endOfMonth(parseISO(this.computedMaxDate))
        )
      }

      if (this.internalInterval === 'quarter') {
        const [year, quarter] = this.selectedQuarter.split('-')

        const dateInQuarter = startOfQuarter(new Date(year, quarter * 3 - 1))

        return isSameDay(
          endOfQuarter(dateInQuarter),
          endOfQuarter(parseISO(this.computedMaxDate))
        )
      }

      if (this.internalInterval === 'year') {
        const dateInYear = startOfYear(new Date(this.yearInternalValue, 0, 1))

        return isSameDay(
          endOfYear(dateInYear),
          endOfYear(parseISO(this.computedMaxDate))
        )
      }

      return false
    },

    shouldDisablePrevArrow() {
      if (this.internalInterval === 'day') {
        return isSameDay(
          parseISO(this.dayInternalValue),
          parseISO(this.minDate)
        )
      }

      if (this.internalInterval === 'week') {
        return isSameDay(
          startOfWeek(parseISO(this.weekInternalValue[0]), {weekStartsOn: 1}),
          startOfWeek(parseISO(this.minDate), {weekStartsOn: 1})
        )
      }

      if (this.internalInterval === 'month') {
        return isSameDay(
          startOfMonth(parseISO(this.monthInternalValue)),
          startOfMonth(parseISO(this.minDate))
        )
      }

      if (this.internalInterval === 'quarter') {
        const [year, quarter] = this.selectedQuarter.split('-')

        const dateInQuarter = startOfQuarter(new Date(year, quarter * 3 - 1))

        return isSameDay(
          startOfQuarter(dateInQuarter),
          startOfQuarter(parseISO(this.minDate))
        )
      }

      if (this.internalInterval === 'year') {
        const dateInYear = startOfYear(new Date(this.yearInternalValue, 0, 1))

        return isSameDay(
          startOfYear(dateInYear),
          startOfYear(parseISO(this.minDate))
        )
      }

      return false
    },

    prevArrowTooltipText() {
      if (this.interval) {
        return this.$t(`previous_${this.interval}`)
      }

      return ''
    },

    nextArrowTooltipText() {
      if (this.interval) {
        return this.$t(`next_${this.interval}`)
      }

      return ''
    },

    currentSelectedText() {
      return this.filteredItems.find(
        (item) => item.value === this.internalInterval
      )?.text
    },

    localizedDateText() {
      if (this.internalInterval === 'now') {
        const start = subHours(new Date(), 1)
        const end = new Date()

        return `${start.toLocaleDateString(this.$i18n.locale, {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: 'numeric',
        })} ${this.$t('to')} ${end.toLocaleTimeString(this.$i18n.locale, {
          hour: 'numeric',
          minute: 'numeric',
        })}`
      } else if (this.internalInterval === 'today') {
        return new Date(startOfToday()).toLocaleDateString(this.$i18n.locale, {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        })
      } else if (this.internalInterval === 'yesterday') {
        return new Date(startOfYesterday()).toLocaleDateString(
          this.$i18n.locale,
          {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          }
        )
      } else if (this.internalInterval === 'day') {
        const date = parseISO(this.dayInternalValue)

        return new Date(date).toLocaleDateString(this.$i18n.locale, {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        })
      } else if (this.internalInterval === 'week') {
        const start = new Date(parseISO(this.weekInternalValue[0]))
        const end = new Date(parseISO(this.weekInternalValue[1]))

        return `${start.toLocaleDateString(this.$i18n.locale, {
          year: undefined,
          month: 'short',
          day: 'numeric',
        })} ${this.$t('to')} ${end.toLocaleDateString(this.$i18n.locale, {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        })}`
      } else if (this.internalInterval === 'month') {
        const [year, month] = this.monthInternalValue.split('-')

        const endOfMonthIsAfterToday = isAfter(
          endOfMonth(new Date(year, month - 1)),
          new Date()
        )

        return `${startOfMonth(new Date(year, month - 1)).toLocaleDateString(
          this.$i18n.locale,
          {
            year: undefined,
            month: 'short',
            day: 'numeric',
          }
        )} ${this.$t('to')} ${
          endOfMonthIsAfterToday
            ? new Date().toLocaleDateString(this.$i18n.locale, {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
              })
            : endOfMonth(new Date(year, month - 1)).toLocaleDateString(
                this.$i18n.locale,
                {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                }
              )
        }`
      } else if (this.internalInterval === 'quarter') {
        const [year, quarter] = this.selectedQuarter.split('-')

        const start = startOfQuarter(new Date(year, quarter * 3 - 1))
        const end = endOfQuarter(new Date(year, quarter * 3 - 1))

        return `${start.toLocaleDateString(this.$i18n.locale, {
          year: undefined,
          month: 'short',
          day: 'numeric',
        })} ${this.$t('to')} ${end.toLocaleDateString(this.$i18n.locale, {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        })} (Q${quarter})`
      } else if (this.internalInterval === 'year') {
        const start = startOfYear(new Date(this.yearInternalValue, 0, 1))
        const end = endOfYear(new Date(this.yearInternalValue, 0, 1))

        const endOfYearIsAfterToday = isAfter(end, new Date())

        return `${start.toLocaleDateString(this.$i18n.locale, {
          year: undefined,
          month: 'short',
          day: 'numeric',
        })} ${this.$t('to')} ${
          endOfYearIsAfterToday
            ? new Date().toLocaleString(this.$i18n.locale, {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
              })
            : end.toLocaleDateString(this.$i18n.locale, {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
              })
        }`
      } else {
        if (!this.internalValue[0] || !this.internalValue[1])
          return this.description

        const start = new Date(parseISO(this.internalValue[0]))
        const end = new Date(parseISO(this.internalValue[1]))

        const startIsAfterEnd = isAfter(start, end)

        if (startIsAfterEnd) {
          return `${end.toLocaleDateString(this.$i18n.locale, {
            year: undefined,
            month: 'short',
            day: 'numeric',
          })} ${this.$t('to')} ${start.toLocaleDateString(this.$i18n.locale, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          })}`
        } else {
          return `${start.toLocaleDateString(this.$i18n.locale, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          })} ${this.$t('to')} ${end.toLocaleDateString(this.$i18n.locale, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          })}`
        }
      }
    },

    defaultActivatorText() {
      const [start, end] = this.value
      if (!start || !end) return this.$t('select_interval')

      if (this.interval === 'now') return this.$t('last_hour')

      if (start === end) {
        return new Date(parseISO(start)).toLocaleDateString(this.$i18n.locale, {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        })
      }

      return `${new Date(parseISO(start)).toLocaleDateString(
        this.$i18n.locale,
        {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        }
      )} ${this.$t('to')} ${new Date(parseISO(end)).toLocaleDateString(
        this.$i18n.locale,
        {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        }
      )}`
    },

    pickerType() {
      return this.internalInterval === 'month' ? 'month' : 'date'
    },

    activeYearStyle() {
      return {
        '--v-picker-year-active-background': this.isCardType
          ? this.color
          : 'var(--accent)',
      }
    },
  },

  watch: {
    interval: {
      immediate: true,
      handler(newVal) {
        this.internalInterval = newVal
      },
    },

    value: {
      immediate: true,
      handler() {
        this.updateDateBasedOnInterval()
      },
    },

    isMenuOpened() {
      this.internalInterval = this.interval

      this.updateDateBasedOnInterval()
    },

    internalInterval: {
      immediate: true,
      handler(newVal, oldVal) {
        this.$emit('update:internal-interval', newVal)

        /**
         * lógica para corrigr um bug do vuetify, onde o picker não manda o valor correto do mês quando mudamos o type para MONTH
         *
         * @see https://github.com/vuetifyjs/vuetify/issues/14388
         */
        const [year, month] = this.today.split('-')
        if (oldVal === 'month') {
          this.pickerDate = `${year}-${month}`
        }

        this.updateDateBasedOnInterval()
      },
    },

    internalValue(newVal) {
      if (newVal.length === 2) {
        this.$emit('update:internal-value', newVal)
      }
    },

    dayInternalValue(newVal) {
      if (newVal) {
        this.$emit('update:internal-value', [newVal, newVal])
      }
    },

    weekInternalValue(newVal) {
      if (newVal.length) {
        this.$emit('update:internal-value', newVal)
      }
    },

    monthInternalValue(newVal) {
      if (newVal) {
        const [year, month] = newVal.split('-')

        const start = startOfMonth(new Date(year, month - 1))
        const end = endOfMonth(new Date(year, month - 1))

        this.$emit('update:internal-value', [
          formatISO(start, {representation: 'date'}),
          formatISO(end, {representation: 'date'}),
        ])
      }
    },

    selectedQuarter(newVal) {
      if (newVal) {
        const [year, quarter] = newVal.split('-')

        const start = startOfQuarter(new Date(year, quarter * 3 - 1))
        const end = endOfQuarter(new Date(year, quarter * 3 - 1))

        this.$emit('update:internal-value', [
          formatISO(start, {representation: 'date'}),
          formatISO(end, {representation: 'date'}),
        ])
      }
    },

    yearInternalValue(newVal) {
      if (newVal) {
        const start = startOfYear(new Date(newVal, 0, 1))
        const end = endOfYear(new Date(newVal, 0, 1))

        this.$emit('update:internal-value', [
          formatISO(start, {representation: 'date'}),
          formatISO(end, {representation: 'date'}),
        ])
      }
    },
  },

  mounted() {
    this.handlePredefinedInterval()
  },

  methods: {
    onInternalIntervalChange(interval) {
      //se o valor é um array, isso significa que o usuário selecionou um intervalo de datas com um range especifico de datas
      //usando a prop `custom-intervals`
      if (Array.isArray(interval)) {
        const [year, month] = interval[0].split('-')

        const [start, end] = interval

        this.internalInterval = 'custom'
        this.pickerDate = `${year}-${month}`
        this.internalValue = [start, end]

        return
      }

      this.internalInterval = interval
    },

    handleDailyIntervalChange() {
      if (this.value.length) {
        const sameDay = isSameDay(
          parseISO(this.value[0]),
          parseISO(this.value[1])
        )

        if (sameDay) {
          this.dayInternalValue = formatISO(parseISO(this.value[0]), {
            representation: 'date',
          })
        } else {
          if (this.dayInternalValue == '') {
            this.dayInternalValue = this.today
          }
        }
      } else {
        this.dayInternalValue = this.today
      }

      const [year, month] = this.dayInternalValue.split('-')
      this.pickerDate = `${year}-${month}`
    },

    handleWeeklyIntervalChange() {
      if (this.value.length) {
        const diff = differenceInDays(
          parseISO(this.value[1]),
          parseISO(this.value[0])
        )

        const sameWeek = isSameWeek(
          parseISO(this.value[0]),
          parseISO(this.value[1]),
          {
            weekStartsOn: 1,
          }
        )

        if (diff === 6 && sameWeek) {
          this.weekInternalValue = [
            formatISO(parseISO(this.value[0]), {
              representation: 'date',
            }),
            formatISO(parseISO(this.value[1]), {
              representation: 'date',
            }),
          ]
        } else {
          if (this.weekInternalValue == '') {
            const startOfPrevWeek = formatISO(
              startOfWeek(subWeeks(new Date(), 1), {
                weekStartsOn: 1,
              }),
              {representation: 'date'}
            )

            const endOfPrevWeek = formatISO(
              endOfWeek(subWeeks(new Date(), 1), {
                weekStartsOn: 1,
              }),
              {representation: 'date'}
            )

            this.weekInternalValue = [startOfPrevWeek, endOfPrevWeek]
          }
        }
      } else {
        const startOfPrevWeek = formatISO(
          startOfWeek(subWeeks(new Date(), 1), {
            weekStartsOn: 1,
          }),
          {representation: 'date'}
        )

        const endOfPrevWeek = formatISO(
          endOfWeek(subWeeks(new Date(), 1), {
            weekStartsOn: 1,
          }),
          {representation: 'date'}
        )

        this.weekInternalValue = [startOfPrevWeek, endOfPrevWeek]
      }

      const [year, month] = this.weekInternalValue[0].split('-')
      this.pickerDate = `${year}-${month}`
    },

    handleMonthlyIntervalChange() {
      const [todayYear] = this.today.split('-')

      const previousMonth = (
        '0' +
        (subMonths(new Date(), 1).getMonth() + 1)
      ).slice(-2)

      if (this.value.length) {
        const [valueYear, valueMonth] = this.value[0].split('-')

        const sameMonth = isSameMonth(
          parseISO(this.value[0]),
          parseISO(this.value[1])
        )

        const diff = differenceInDays(
          parseISO(this.value[1]),
          parseISO(this.value[0])
        )

        const daysOfMonth = getDaysInMonth(parseISO(this.value[0]))

        if (sameMonth && diff + 1 === daysOfMonth) {
          this.monthInternalValue = `${valueYear}-${valueMonth}`
        } else {
          if (this.monthInternalValue == '') {
            this.monthInternalValue = `${todayYear}-${previousMonth}`
          }
        }
      } else {
        this.monthInternalValue = `${todayYear}-${previousMonth}`
      }

      this.pickerDate = this.monthInternalValue
    },

    handleQuarterlyIntervalChange() {
      const isTodayBeforeEndOfQuarter = isBefore(
        endOfToday(),
        endOfQuarter(add(startOfToday(), {days: 1}))
      )

      const [todayYear] = this.today.split('-')
      const availableQuarter = isTodayBeforeEndOfQuarter
        ? getQuarter(subQuarters(startOfToday(), 1))
        : getQuarter(startOfToday())

      if (this.value.length) {
        const [valueYear] = this.value[0].split('-')

        const sameQuarter = isSameQuarter(
          parseISO(this.value[0]),
          parseISO(this.value[1])
        )

        const isStartOfQuarter = isSameDay(
          parseISO(this.value[0]),
          startOfQuarter(parseISO(this.value[0]))
        )

        const isEndOfQuarter = isSameDay(
          parseISO(this.value[1]),
          endOfQuarter(parseISO(this.value[1]))
        )

        const valueQuarter = getQuarter(parseISO(this.value[0]))

        if (sameQuarter && isStartOfQuarter && isEndOfQuarter) {
          this.selectedQuarter = `${valueYear}-${valueQuarter}`
        } else {
          if (this.selectedQuarter == '') {
            this.selectedQuarter = `${todayYear}-${availableQuarter}`
          }
        }
      } else {
        this.selectedQuarter = `${todayYear}-${availableQuarter}`
      }
    },

    handleYearlyIntervalChange() {
      const prevYear = subYears(new Date(), 1).getFullYear()

      if (this.value.length) {
        const [valueYear] = this.value[0].split('-')

        const sameYear = isSameYear(
          parseISO(this.value[0]),
          parseISO(this.value[1])
        )
        const isStartOfYear = isSameDay(
          parseISO(this.value[0]),
          startOfYear(parseISO(this.value[0]))
        )
        const isEndOfYear = isSameDay(
          parseISO(this.value[1]),
          endOfYear(parseISO(this.value[1]))
        )

        if (sameYear && isStartOfYear && isEndOfYear) {
          this.yearInternalValue = valueYear
        } else {
          if (this.yearInternalValue == '') {
            this.yearInternalValue = prevYear
          }
        }
      } else {
        this.yearInternalValue = prevYear
      }
    },

    logInvalidInterval() {
      console.error(
        'Periodo inválido para o intervalo selecionado',
        '\n',
        'O componente irá assumir um valor padrão para o intervalo selecionado',
        '\n',
        `interval: ${this.interval}`,
        '\n',
        `data inicial: ${this.value[0]}`,
        '\n',
        `data final: ${this.value[1]}`
      )
    },

    /**
     *
     * este metodo faz os tratamentos necessários para quando o interval/value
     * forem definidos por um agente externo
     *
     * @example <z-date-range-filter :interval="week" :value="['2023-11-13', '2023-11-18']">
     *
     * Nesse caso, o componente irá verificar se o value é um intervalo válido para o intervalo selecionado
     * caso não seja, o componente irá assumir um valor padrão para o intervalo selecionado e irá logar um erro no console
     */
    handlePredefinedInterval() {
      if (this.internalInterval === 'now') {
        this.$emit('input', [
          formatISO(subHours(new Date(), 1)),
          formatISO(new Date()),
        ])
      }

      if (this.internalInterval === 'day') {
        if (this.value.length) {
          const sameDay = isSameDay(
            parseISO(this.value[0]),
            parseISO(this.value[1])
          )

          if (!sameDay) {
            this.logInvalidInterval()
          }
        } else {
          this.logInvalidInterval()
        }
      }
      if (this.internalInterval === 'today') {
        this.$emit('input', [this.today, this.today])
      }
      if (this.internalInterval === 'yesterday') {
        this.$emit('input', [
          formatISO(startOfYesterday(), {representation: 'date'}),
          formatISO(endOfYesterday(), {representation: 'date'}),
        ])
      }
      if (this.internalInterval === 'week') {
        if (this.value.length) {
          const diff = differenceInDays(
            parseISO(this.value[1]),
            parseISO(this.value[0])
          )

          const sameWeek = isSameWeek(
            parseISO(this.value[0]),
            parseISO(this.value[1]),
            {weekStartsOn: 1}
          )

          if (diff !== 6 || !sameWeek) {
            this.logInvalidInterval()
          }
        } else {
          this.logInvalidInterval()
        }
      }
      if (this.internalInterval === 'month') {
        if (this.value.length) {
          const sameMonth = isSameMonth(
            parseISO(this.value[0]),
            parseISO(this.value[1])
          )

          const diff = differenceInDays(
            parseISO(this.value[1]),
            parseISO(this.value[0])
          )

          const daysOfMonth = getDaysInMonth(parseISO(this.value[0]))

          if (!sameMonth || diff + 1 !== daysOfMonth) {
            this.logInvalidInterval()
          }
        } else {
          this.logInvalidInterval()
        }
      }
      if (this.internalInterval === 'quarter') {
        if (this.value.length) {
          const sameQuarter = isSameQuarter(
            parseISO(this.value[0]),
            parseISO(this.value[1])
          )

          const isStartOfQuarter = isSameDay(
            parseISO(this.value[0]),
            startOfQuarter(parseISO(this.value[0]))
          )

          const isEndOfQuarter = isSameDay(
            parseISO(this.value[1]),
            endOfQuarter(parseISO(this.value[1]))
          )

          if (!sameQuarter || !isStartOfQuarter || !isEndOfQuarter) {
            this.logInvalidInterval()
          }
        } else {
          this.logInvalidInterval()
        }
      }
      if (this.internalInterval === 'year') {
        if (this.value.length) {
          const sameYear = isSameYear(
            parseISO(this.value[0]),
            parseISO(this.value[1])
          )
          const isStartOfYear = isSameDay(
            parseISO(this.value[0]),
            startOfYear(parseISO(this.value[0]))
          )
          const isEndOfYear = isSameDay(
            parseISO(this.value[1]),
            endOfYear(parseISO(this.value[1]))
          )

          if (sameYear && isStartOfYear && isEndOfYear) {
            this.logInvalidInterval()
          }
        } else {
          this.logInvalidInterval()
        }
      }
    },

    updateDateBasedOnInterval() {
      const [year, month] = this.today.split('-')

      switch (this.internalInterval) {
        case 'now':
        case 'today':
          this.internalValue = this.today
          this.pickerDate = `${year}-${month}`
          break
        case 'yesterday':
          this.internalValue = formatISO(startOfYesterday(), {
            representation: 'date',
          })
          this.pickerDate = `${year}-${month}`
          break
        case 'day':
          this.handleDailyIntervalChange()
          break
        case 'week':
          this.handleWeeklyIntervalChange()
          break
        case 'month':
          this.handleMonthlyIntervalChange()
          break
        case 'quarter':
          this.handleQuarterlyIntervalChange()
          break
        case 'year':
          this.handleYearlyIntervalChange()
          break
        case 'custom':
          if (this.value.length === 2) {
            this.internalValue = [...this.value]
            const [valueYear, valueMonth] = this.value[0].split('-')
            this.pickerDate = `${valueYear}-${valueMonth}`
          } else {
            this.internalValue = []
            this.pickerDate = `${year}-${month}`
          }
          break
      }
    },

    onConfirm() {
      const eventName = this.isCardType ? 'click:button' : 'input'

      if (this.internalInterval === 'now') {
        this.$emit(eventName, [
          formatISO(subHours(new Date(), 1)),
          formatISO(new Date()),
        ])
      }
      if (this.internalInterval === 'today') {
        this.$emit(eventName, [this.today, this.today])
      }
      if (this.internalInterval === 'yesterday') {
        this.$emit(eventName, [
          formatISO(startOfYesterday(), {representation: 'date'}),
          formatISO(endOfYesterday(), {representation: 'date'}),
        ])
      }
      if (this.internalInterval === 'day') {
        this.$emit(eventName, [this.dayInternalValue, this.dayInternalValue])
      }
      if (this.internalInterval === 'week') {
        this.$emit(eventName, this.weekInternalValue)
      }
      if (this.internalInterval === 'month') {
        const [year, month] = this.monthInternalValue.split('-')

        this.$emit(eventName, [
          formatISO(startOfMonth(new Date(year, month - 1)), {
            representation: 'date',
          }),
          formatISO(endOfMonth(new Date(year, month - 1)), {
            representation: 'date',
          }),
        ])
      }
      if (this.internalInterval === 'quarter') {
        const [year, q] = this.selectedQuarter.split('-')

        const start = formatISO(startOfQuarter(new Date(year, q * 3 - 1)), {
          representation: 'date',
        })
        const end = formatISO(endOfQuarter(new Date(year, q * 3 - 1)), {
          representation: 'date',
        })

        this.$emit(eventName, [start, end])
      }
      if (this.internalInterval === 'year') {
        this.$emit(eventName, [
          formatISO(startOfYear(new Date(this.yearInternalValue, 0)), {
            representation: 'date',
          }),
          formatISO(endOfYear(new Date(this.yearInternalValue, 0)), {
            representation: 'date',
          }),
        ])
      }
      if (this.internalInterval === 'custom') {
        const startIsAfterEnd = isAfter(
          new Date(parseISO(this.internalValue[0])),
          new Date(parseISO(this.internalValue[1]))
        )

        if (startIsAfterEnd) {
          this.$emit(eventName, [this.internalValue[1], this.internalValue[0]])
        } else {
          this.$emit(eventName, this.internalValue)
        }
      }

      this.$emit('update:interval', this.internalInterval)

      this.isMenuOpened = false
    },

    onCancel() {
      this.isMenuOpened = false
    },

    onInputDate(val) {
      if (this.internalInterval === 'week') {
        const date = parseISO(val[0])

        const enfOfWeekIsAfterToday = isAfter(
          endOfWeek(date, {weekStartsOn: 1}),
          new Date()
        )

        this.weekInternalValue = [
          formatISO(startOfWeek(date, {weekStartsOn: 1}), {
            representation: 'date',
          }),
          enfOfWeekIsAfterToday
            ? this.today
            : formatISO(endOfWeek(date, {weekStartsOn: 1}), {
                representation: 'date',
              }),
        ]
      }

      if (this.internalInterval === 'custom') {
        this.internalValue = val
      }
    },

    onChangeDate(value) {
      if (this.internalInterval === 'day') {
        this.dayInternalValue = value
      }

      /**
        week é definido no método **onInputDate**,
        pois o vuetify só emite o valor (quando a prop range é true)
        se o usuário seleciona as datas de inico e fim
       */

      if (this.internalInterval === 'month') {
        this.monthInternalValue = value
      }

      if (this.internalInterval === 'year') {
        this.yearInternalValue = value
      }

      if (this.internalInterval === 'custom') {
        //atualização do valor interno é feito no método **onInputDate**
      }
    },

    onClickPrevArrow() {
      if (this.interval === 'day') {
        const day = this.value[0]
        const prevDay = subDays(parseISO(day), 1)
        if (isBefore(prevDay, parseISO(this.minDate))) return

        this.dayInternalValue = formatISO(prevDay, {representation: 'date'})
        this.$emit('input', [this.dayInternalValue, this.dayInternalValue])
      }

      if (this.interval === 'week') {
        const [start] = this.value
        const startOfPrevWeek = startOfWeek(subWeeks(parseISO(start), 1), {
          weekStartsOn: 1,
        })

        const endOfPrevWeek = endOfWeek(startOfPrevWeek, {weekStartsOn: 1})

        if (isBefore(startOfPrevWeek, parseISO(this.minDate))) return

        this.weekInternalValue = [
          formatISO(startOfPrevWeek, {representation: 'date'}),
          formatISO(endOfPrevWeek, {representation: 'date'}),
        ]

        this.$emit('input', this.weekInternalValue)
      }

      if (this.interval === 'month') {
        const [year, month] = this.monthInternalValue.split('-')

        const prevMonth = subMonths(new Date(year, month - 1), 1)

        const start = startOfMonth(prevMonth)
        const end = endOfMonth(prevMonth)

        if (isBefore(start, parseISO(this.minDate))) return

        const startYear = start.getFullYear()
        const startMonth = ('0' + (start.getMonth() + 1)).slice(-2)

        this.monthInternalValue = `${startYear}-${startMonth}`

        this.$emit('input', [
          formatISO(start, {representation: 'date'}),
          formatISO(end, {representation: 'date'}),
        ])
      }

      if (this.interval === 'quarter') {
        const [year, quarter] = this.selectedQuarter.split('-')

        const prevQuarter = subQuarters(new Date(year, quarter * 3 - 1), 1)

        const start = startOfQuarter(prevQuarter)
        const end = endOfQuarter(prevQuarter)

        if (isBefore(start, parseISO(this.minDate))) return

        const startYear = start.getFullYear()
        const startQuarter = getQuarter(start)

        this.selectedQuarter = `${startYear}-${startQuarter}`

        this.$emit('input', [
          formatISO(start, {representation: 'date'}),
          formatISO(end, {representation: 'date'}),
        ])
      }

      if (this.interval === 'year') {
        const prevYear = subYears(new Date(this.yearInternalValue, 0), 1)

        const start = startOfYear(prevYear)
        const end = endOfYear(prevYear)

        if (isBefore(start, parseISO(this.minDate))) return

        this.yearInternalValue = start.getFullYear()

        this.$emit('input', [
          formatISO(start, {representation: 'date'}),
          formatISO(end, {representation: 'date'}),
        ])
      }
    },
    onClickNextArrow() {
      if (this.interval === 'day') {
        const day = this.value[0]
        const nextDay = addDays(parseISO(day), 1)
        if (isAfter(nextDay, parseISO(this.maxDate))) return

        this.dayInternalValue = formatISO(nextDay, {representation: 'date'})
        this.$emit('input', [this.dayInternalValue, this.dayInternalValue])
      }

      if (this.interval === 'week') {
        const [start] = this.value
        const startOfNextWeek = startOfWeek(addWeeks(parseISO(start), 1), {
          weekStartsOn: 1,
        })

        const endOfNextWeek = endOfWeek(startOfNextWeek, {weekStartsOn: 1})

        if (isAfter(endOfNextWeek, parseISO(this.maxDate))) return

        this.weekInternalValue = [
          formatISO(startOfNextWeek, {representation: 'date'}),
          formatISO(endOfNextWeek, {representation: 'date'}),
        ]

        this.$emit('input', this.weekInternalValue)
      }

      if (this.interval === 'month') {
        const [year, month] = this.monthInternalValue.split('-')

        const nextMonth = addMonths(new Date(year, month - 1), 1)

        const start = startOfMonth(nextMonth)
        const end = endOfMonth(nextMonth)

        if (isAfter(end, parseISO(this.maxDate))) return

        const startYear = start.getFullYear()
        const startMonth = ('0' + (start.getMonth() + 1)).slice(-2)

        this.monthInternalValue = `${startYear}-${startMonth}`

        this.$emit('input', [
          formatISO(start, {representation: 'date'}),
          formatISO(end, {representation: 'date'}),
        ])
      }

      if (this.interval === 'quarter') {
        const [year, quarter] = this.selectedQuarter.split('-')

        const nextQuarter = addQuarters(new Date(year, quarter * 3 - 1), 1)

        const start = startOfQuarter(nextQuarter)
        const end = endOfQuarter(nextQuarter)

        if (isAfter(end, parseISO(this.maxDate))) return

        const startYear = start.getFullYear()
        const startQuarter = getQuarter(start)

        this.selectedQuarter = `${startYear}-${startQuarter}`

        this.$emit('input', [
          formatISO(start, {representation: 'date'}),
          formatISO(end, {representation: 'date'}),
        ])
      }

      if (this.interval === 'year') {
        const nextYear = addYears(new Date(this.yearInternalValue, 0), 1)

        const start = startOfYear(nextYear)
        const end = endOfYear(nextYear)

        if (isAfter(end, parseISO(this.maxDate))) return

        this.yearInternalValue = start.getFullYear()

        this.$emit('input', [
          formatISO(start, {representation: 'date'}),
          formatISO(end, {representation: 'date'}),
        ])
      }
    },
  },

  i18n: {
    messages: {
      en: {
        last_hour: 'Last hour',
        select_interval: 'Select interval',
        to: 'to',
        data_from_last_hour: 'Data from last hour',
        cancel: 'Cancel',
        confirm: 'Confirm',
        now: 'Now',
        today: 'Today',
        yesterday: 'Yesterday',
        day: 'By day',
        week: 'By week',
        month: 'By month',
        months: 'months',
        quarter: ' By quarter',
        year: 'By year',
        custom: 'Custom period',
        compare: 'Compare',
        previous_day: 'Previous day',
        previous_week: 'Previous week',
        previous_month: 'Previous month',
        previous_quarter: 'Previous quarter',
        previous_year: 'Previous year',
        next_day: 'Next day',
        next_week: 'Next week',
        next_month: 'Next month',
        next_quarter: 'Next quarter',
        next_year: 'Next year',
      },
      pt: {
        last_hour: 'Última hora',
        select_interval: 'Selecione um intervalo',
        to: 'até',
        data_from_last_hour: 'Dados da última hora',
        cancel: 'Cancelar',
        confirm: 'Confirmar',
        now: 'Agora',
        today: 'Hoje',
        yesterday: 'Ontem',
        day: 'Por dia',
        week: 'Por semana',
        month: 'Por mês',
        months: 'meses',
        quarter: 'Por trimestre',
        year: 'Por ano',
        custom: 'Período personalizado',
        compare: 'Comparar',
        previous_day: 'Dia anterior',
        previous_week: 'Semana anterior',
        previous_month: 'Mês anterior',
        previous_quarter: 'Trimestre anterior',
        previous_year: 'Ano anterior',
        next_day: 'Próximo dia',
        next_week: 'Próxima semana',
        next_month: 'Próximo mês',
        next_quarter: 'Próximo trimestre',
        next_year: 'Próximo ano',
      },

      es: {
        last_hour: 'Ultima hora',
        select_interval: 'Seleccione un intervalo',
        to: 'hasta',
        data_from_last_hour: 'Datos de la última hora',
        cancel: 'Cancelar',
        confirm: 'Confirmar',
        now: 'Ahora',
        today: 'Hoy',
        yesterday: 'Ayer',
        day: 'Por día',
        week: 'Por semana',
        month: 'Por mes',
        months: 'meses',
        quarter: 'Por trimestre',
        year: 'Por año',
        custom: 'Período personalizado',
        compare: 'Comparar',
        previous_day: 'Día anterior',
        previous_week: 'Semana anterior',
        previous_month: 'Mes anterior',
        previous_quarter: 'Trimestre anterior',
        previous_year: 'Año anterior',
        next_day: 'Próximo día',
        next_week: 'Próxima semana',
        next_month: 'Próximo mes',
        next_quarter: 'Próximo trimestre',
        next_year: 'Próximo año',
      },
      fr: {
        last_hour: 'Dernière heure',
        select_interval: 'Sélectionner un intervalle',
        to: 'à',
        data_from_last_hour: 'Données de la dernière heure',
        cancel: 'Annuler',
        confirm: 'Confirmer',
        now: 'Maintenant',
        today: "Aujourd'hui",
        yesterday: 'Hier',
        day: 'Par jour',
        week: 'Par semaine',
        month: 'Par mois',
        months: 'mois',
        quarter: 'Par trimestre',
        year: 'Par an',
        custom: 'Période personnalisée',
        compare: 'Comparer',
        previous_day: 'Jour précédent',
        previous_week: 'Semaine précédente',
        previous_month: 'Mois précédent',
        previous_quarter: 'Trimestre précédent',
        previous_year: 'Année précédente',
        next_day: 'Jour suivant',
        next_week: 'Semaine suivante',
        next_month: 'Mois suivant',
        next_quarter: 'Trimestre suivant',
        next_year: 'Année suivante',
      },
      tr: {
        last_hour: 'Son saat',
        select_interval: 'Aralık seç',
        to: 'ila',
        data_from_last_hour: 'Son saat verisi',
        cancel: 'İptal',
        confirm: 'Onayla',
        now: 'Şimdi',
        today: 'Bugün',
        yesterday: 'Dün',
        day: 'Günlük',
        week: 'Haftalık',
        month: 'Aylık',
        months: 'aylar',
        quarter: 'Çeyrek',
        year: 'Yıllık',
        custom: 'Özel dönem',
        compare: 'Karşılaştır',
        previous_day: 'Önceki gün',
        previous_week: 'Önceki hafta',
        previous_month: 'Önceki ay',
        previous_quarter: 'Önceki çeyrek',
        previous_year: 'Önceki yıl',
        next_day: 'Sonraki gün',
        next_week: 'Sonraki hafta',
        next_month: 'Sonraki ay',
        next_quarter: 'Sonraki çeyrek',
        next_year: 'Sonraki yıl',
      },
    },
  },
}
</script>

<style scoped lang="scss">
.default-activator {
  display: flex;
  align-items: center;
  gap: 4px;
  user-select: none;

  .prev-icon-container,
  .next-icon-container {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    border: 1px solid black;
    border-radius: 50%;

    &.disabled {
      opacity: 0.5;
      pointer-events: none;
    }
  }
}

.truncate {
  height: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.arrow-menu {
  transform: translateX(-50%);
  position: absolute;
  border-bottom: solid 8px var(--white) !important;
  border-right: solid 8px transparent;
  border-left: solid 8px transparent;
  top: 30px;
  left: 50%;
  height: 0px;
  width: 0px;
  z-index: 9;
}
</style>
